import { CommonModule } from '@angular/common';
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { filter, finalize, Subject, Subscription, distinctUntilChanged, debounceTime, switchMap, of } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { AuthService } from '../../core/services/auth.service';
import { AuthTokenService } from '../../core/services/auth-token.service';
import { I18nService } from '../../core/services/i18n.service';
import { NotificationService } from '../../core/services/notification.service';
import { PermissionService } from '../../core/services/permission.service';
import { GlobalSearchResultItem, GlobalSearchService } from '../../core/services/global-search.service';

interface NavItem {
  labelKey: string;
  icon: string;
  route: string;
  permission?: string;
  permissionAny?: string[];
  hidden?: boolean;
}

@Component({
  selector: 'app-shell',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    RouterLinkActive,
    RouterOutlet,
    TranslateModule,
    ButtonModule,
    DropdownModule,
    InputTextModule
  ],
  templateUrl: './app-shell.component.html',
  styleUrl: './app-shell.component.scss'
})
export class AppShellComponent implements OnInit, OnDestroy {
  private static readonly sidebarStateStorageKey = 'inbordo.admin.sidebarCollapsed.v1';
  readonly searchTypeOptions: Array<{ label: string; value: string }> = [
    { label: 'All', value: 'all' },
    { label: 'Members', value: 'member' },
    { label: 'Memberships', value: 'membership' },
    { label: 'Packages', value: 'package' },
    { label: 'Sales', value: 'invoice' },
    { label: 'Measurements', value: 'measurement' }
  ];
  readonly navItems: NavItem[] = [
    { labelKey: 'nav.dashboard', icon: 'pi pi-home', route: '/dashboard' },
    { labelKey: 'nav.applications', icon: 'pi pi-inbox', route: '/applications', permission: 'MEMBERSHIP_READ' },
    { labelKey: 'nav.members', icon: 'pi pi-users', route: '/members', permission: 'MEMBER_READ' },
    { labelKey: 'nav.memberships', icon: 'pi pi-id-card', route: '/memberships', permission: 'MEMBERSHIP_READ', hidden: true },
    { labelKey: 'nav.sales', icon: 'pi pi-shopping-cart', route: '/sales', permission: 'BILLING_READ' },
    { labelKey: 'nav.packages', icon: 'pi pi-box', route: '/packages', permission: 'PACKAGE_READ' },
    { labelKey: 'nav.measurements', icon: 'pi pi-chart-line', route: '/measurements', permission: 'MEASURE_READ' },
    { labelKey: 'nav.staff', icon: 'pi pi-users', route: '/staff', permissionAny: ['PT_READ', 'EMPLOYEE_READ'] },
    { labelKey: 'nav.classes', icon: 'pi pi-calendar', route: '/classes', permission: 'CLASS_READ' },
    { labelKey: 'nav.reports', icon: 'pi pi-chart-bar', route: '/reports', permission: 'REPORT_READ' },
    { labelKey: 'nav.masters', icon: 'pi pi-database', route: '/masters', permission: 'SETTINGS_READ' },
    { labelKey: 'nav.contentStudio', icon: 'pi pi-images', route: '/content-studio', permission: 'SETTINGS_READ', hidden: true },
    { labelKey: 'nav.tenantSettings', icon: 'pi pi-sliders-h', route: '/tenant-settings', permission: 'SETTINGS_READ' },
    { labelKey: 'nav.branchSettings', icon: 'pi pi-map-marker', route: '/branch-settings', permission: 'SETTINGS_READ' },
    { labelKey: 'nav.rolesTrials', icon: 'pi pi-shield', route: '/roles-trials', permission: 'SETTINGS_READ', hidden: true },
    { labelKey: 'nav.auditLogs', icon: 'pi pi-clipboard', route: '/audit-logs', permission: 'AUDIT_READ' },
    { labelKey: 'nav.notificationModule', icon: 'pi pi-send', route: '/notifications-module', permission: 'SETTINGS_READ' },
    { labelKey: 'nav.notificationCenter', icon: 'pi pi-bell', route: '/notifications', permission: 'SETTINGS_READ', hidden: true },
    { labelKey: 'nav.designSystem', icon: 'pi pi-palette', route: '/design-system', hidden: true },
    { labelKey: 'nav.checkinLogs', icon: 'pi pi-history', route: '/checkin-logs' },
    { labelKey: 'nav.accessControl', icon: 'pi pi-lock', route: '/access-control', permission: 'SETTINGS_READ' }
  ];
  visibleNavItems: NavItem[] = [];

  signingOut = false;
  deactivatingAccount = false;
  showQuickActions = false;
  searchQuery = '';
  searchType = 'all';
  searchResults: GlobalSearchResultItem[] = [];
  showSearchResults = false;
  searchLoading = false;
  routeLoading = false;
  sidebarCollapsed = this.readSidebarState();
  private searchInput$ = new Subject<{ query: string; type: string }>();
  private searchSub?: Subscription;

  constructor(
    private readonly authService: AuthService,
    private readonly tokenStore: AuthTokenService,
    private readonly notifications: NotificationService,
    private readonly i18n: I18nService,
    private readonly translate: TranslateService,
    private readonly permissions: PermissionService,
    private readonly router: Router,
    private readonly globalSearch: GlobalSearchService
  ) {}

  ngOnInit(): void {
    const guarded = Array.from(
      new Set(
        this.navItems.flatMap((item) => {
          if (item.permission) {
            return [item.permission];
          }
          return item.permissionAny ?? [];
        })
      )
    );
    this.permissions.hasPermissions(guarded).subscribe((results) => {
      this.visibleNavItems = this.navItems.filter((item) => {
        if (item.hidden) {
          return false;
        }
        if (!item.permission && !item.permissionAny?.length) {
          return true;
        }
        if (item.permission) {
          return results[item.permission] ?? false;
        }
        return (item.permissionAny ?? []).some((code) => results[code] ?? false);
      });
    });

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.routeLoading = true;
        return;
      }
      if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        this.routeLoading = false;
      }
      if (event instanceof NavigationEnd) {
        this.cleanupOverlays();
      }
    });

    this.searchSub = this.searchInput$
      .pipe(
        debounceTime(250),
        distinctUntilChanged((a, b) => a.query === b.query && a.type === b.type),
        switchMap(({ query, type }) => {
          const trimmed = query.trim();
          if (trimmed.length < 2) {
            this.searchResults = [];
            this.showSearchResults = false;
            return of([]);
          }
          this.searchLoading = true;
          const types = type === 'all' ? undefined : [type];
          return this.globalSearch.search(trimmed, 20, types).pipe(finalize(() => (this.searchLoading = false)));
        })
      )
      .subscribe((results) => {
        this.searchResults =
          this.searchType === 'all'
            ? results
            : results.filter((item) => item.itemType === (this.searchType as GlobalSearchResultItem['itemType']));
        this.showSearchResults = results.length > 0;
      });

  }

  ngOnDestroy(): void {
    this.searchSub?.unsubscribe();
  }

  toggleSidebar(): void {
    this.sidebarCollapsed = !this.sidebarCollapsed;
    try {
      window.localStorage.setItem(
        AppShellComponent.sidebarStateStorageKey,
        this.sidebarCollapsed ? '1' : '0'
      );
    } catch {
      // Keep the current state when browser storage is unavailable.
    }
  }

  private readSidebarState(): boolean {
    try {
      return window.localStorage.getItem(AppShellComponent.sidebarStateStorageKey) === '1';
    } catch {
      return false;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.cleanupOverlays();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (!target) {
      return;
    }
    if (!target.closest('.search')) {
      this.showSearchResults = false;
    }
    if (!target.closest('.quick-actions')) {
      this.showQuickActions = false;
    }
    if (document.querySelector('.upload-modal-backdrop')) {
      return;
    }
    if (target.closest('.confirm-card') || target.closest('.modal-card') || this.isFloatingPanelTarget(target)) {
      return;
    }
  }

  toggleQuickActions(): void {
    this.showQuickActions = !this.showQuickActions;
  }

  closeQuickActions(): void {
    this.showQuickActions = false;
  }

  onSearchInput(value: string): void {
    this.searchQuery = value;
    this.searchInput$.next({ query: value, type: this.searchType });
    if (!value.trim()) {
      this.searchResults = [];
      this.showSearchResults = false;
    }
  }

  onSearchTypeChange(value: string): void {
    this.searchType = value;
    this.searchResults = [];
    this.showSearchResults = false;
    this.searchInput$.next({ query: this.searchQuery, type: this.searchType });
  }

  onSearchFocus(): void {
    if (this.searchResults.length > 0) {
      this.showSearchResults = true;
    }
  }

  openFirstSearchResult(): void {
    if (this.searchResults.length === 0) {
      return;
    }
    this.openSearchResult(this.searchResults[0]);
  }

  openSearchResult(item: GlobalSearchResultItem): void {
    this.showSearchResults = false;
    this.searchQuery = '';
    this.searchResults = [];

    switch (item.itemType) {
      case 'member':
        this.router.navigate(['/members', item.itemId]);
        return;
      case 'membership':
        this.router.navigate(['/memberships', item.itemId]);
        return;
      case 'package':
        this.router.navigate(['/packages'], { queryParams: { packageId: item.itemId } });
        return;
      case 'invoice':
        this.router.navigate(['/sales'], { queryParams: { invoiceId: item.itemId } });
        return;
      case 'measurement':
        this.router.navigate(['/members', item.itemId], { queryParams: { tab: 'measurements' } });
        return;
      default:
        return;
    }
  }

  private cleanupOverlays(): void {
    document
      .querySelectorAll('.modal-backdrop, .confirm-backdrop, .upload-modal-backdrop, .sub-modal-backdrop')
      .forEach((node) => node.remove());
  }

  private isFloatingPanelTarget(target: HTMLElement): boolean {
    return Boolean(
      target.closest('.p-overlay') ||
        target.closest('.p-overlay-content') ||
        target.closest('.p-dropdown-panel') ||
        target.closest('.p-dropdown') ||
        target.closest('.p-multiselect') ||
        target.closest('.p-select') ||
        target.closest('.p-calendar') ||
        target.closest('.p-datepicker') ||
        target.closest('.p-datepicker-panel') ||
        target.closest('.p-inputwrapper')
    );
  }

  get localeLabel(): string {
    return this.i18n.currentLocale.toUpperCase();
  }

  toggleLocale(): void {
    this.i18n.toggleLocale();
  }

  logout(): void {
    if (this.signingOut) {
      return;
    }

    this.signingOut = true;
    this.authService
      .logout()
      .pipe(finalize(() => (this.signingOut = false)))
      .subscribe({
        next: () => {
          this.tokenStore.clear();
          this.permissions.clear();
          this.notifications.info(this.translate.instant('auth.messages.signedOut'));
          this.router.navigateByUrl('/login');
        },
        error: () => {
          this.tokenStore.clear();
          this.permissions.clear();
          this.router.navigateByUrl('/login');
        }
      });
  }

  deactivateAccount(): void {
    if (this.deactivatingAccount) {
      return;
    }

    this.deactivatingAccount = true;
    this.authService
      .deactivateAccount()
      .pipe(finalize(() => (this.deactivatingAccount = false)))
      .subscribe({
        next: (response) => {
          // console.log('[Account Delete API] Request', {
          //   method: 'DELETE',
          //   url: '/api/accounts/self',
          //   body: null
          // });
          // console.log('[Account Delete API] Response payload', response);
          this.tokenStore.clear();
          this.permissions.clear();
          this.notifications.info(this.translate.instant('auth.messages.accountDeactivated'));
          this.router.navigateByUrl('/login');
        },
        error: (error) => {
          // console.error('[Account Delete API] Failed', {
          //   method: 'DELETE',
          //   url: '/api/accounts/self',
          //   status: error?.status,
          //   payload: error?.error,
          //   message: error?.message
          // });
          this.tokenStore.clear();
          this.permissions.clear();
          this.router.navigateByUrl('/login');
        }
      });
  }
}
